import {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {FormikHelpers, FormikProps} from 'formik';
import {Popup, Switcher, Button} from '@tickchak/react-share/app';
import {XHR_LOADING_STATE, XHR_SUCCESS_STATE} from '../../../../assets/constants/constants';
import {TXHR} from '../../../../assets/constants/constansts.type';
import useShow, {TShow} from '../../../../hooks/useShow';
import KebabDots from '../../../../assets/icons/KebabDots';
import FormContext from '../../../components/formContext/FormContext';
import DetailsTab from './tabs/DetailsTab';
import TicketsTab from './tabs/TicketsTab';
import PlaceholderTab from './tabs/PlaceholderTab';
import LecturersSelectPopup from './fields/LecturersSelectPopup';
import HallSelectPopup from './fields/HallSelectPopup';
import TimeSlotSelectPopup from './fields/TimeSlotSelectPopup';
import DaySelectPopup from './fields/DaySelectPopup';
import {initialValueLecture, buildValidationSchema} from './constants';
import {LectureFormValues, TabId} from './types';
import {createLecture, updateLecture} from '../../../../redux/timetable/timetableActions';
import {RootState} from '../../../../redux/store.type';
import {TSlot, TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {combineDateTime, getStartOfDay} from '../../../../library/dates';
import {useTimetableDays} from '../../hooks/useTimetableDays';
import * as css from './ManageLecturePopup.css';

interface ManageLecturePopupProps {
    show: TShow;
    lecture?: TTimetableItem | null;
    defaultTsid?: number | null;
}

const ManageLecturePopup: FC<ManageLecturePopupProps> = ({show, lecture, defaultTsid}) => (
    <Popup show={show} data-cy="manageLecturePop">
        <PopupContent key={String(show.isOpen)} show={show} lecture={lecture ?? null} defaultTsid={defaultTsid ?? null} />
    </Popup>
);

const PopupContent: FC<{show: TShow; lecture: TTimetableItem | null; defaultTsid: number | null}> = ({show, lecture, defaultTsid}) => {
    const {t} = useTranslation('timetable');
    const isEdit = !!lecture;
    const [activeTab, setActiveTab] = useState<TabId>('details');
    const lecturersShow = useShow();
    const hallShow = useShow();
    const slotShow = useShow();
    const dayShow = useShow();
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const {conferenceDays, currentDayIndex} = useTimetableDays();

    const initialValues: LectureFormValues = {
        ...initialValueLecture(lecture),
        tsid: lecture?.tsid ?? defaultTsid,
        dayIndex: lecture ? conferenceDays.findIndex(d => d.timestamp === getStartOfDay(lecture.time)) : currentDayIndex,
    };

    const handleSubmit = async (_changed: any, values: LectureFormValues, helpers: FormikHelpers<LectureFormValues>) => {
        const slot = slots.find((s: TSlot) => s.tsid === values.tsid);
        const day = conferenceDays[values.dayIndex];
        if(!slot || !day) return;
        helpers.setSubmitting(true);
        try {
            const {hasCasualPrice, title, link, priceCasual, dayIndex: _dayIndex, ...rest} = values;
            const payload = {
                ...rest,
                title: (title ?? '').trim(),
                link: (link ?? '').trim() || null,
                priceCasual: hasCasualPrice ? priceCasual : null,
                time: combineDateTime(day.timestamp, slot.timeStart),
            };
            const result = isEdit
                ? await updateLecture(lecture!.ttid, payload)
                : await createLecture(payload);
            if(result) show.close();
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const tabs: {id: TabId; label: string; content: JSX.Element}[] = [
        {id: 'details', label: t('tabDetails'), content: <DetailsTab lecturersShow={lecturersShow} hallShow={hallShow} slotShow={slotShow} dayShow={dayShow} />},
        {id: 'tickets', label: t('tabTickets'), content: <TicketsTab />},
        {id: 'advanced', label: t('tabAdvanced'), content: <PlaceholderTab />},
    ];
    const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

    return (
        <FormContext
            initialValues={initialValues}
            validationSchema={buildValidationSchema(t('fieldRequired'))}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({submitForm, isSubmitting, values, setFieldValue}: FormikProps<LectureFormValues>) => (
                <Popup.Tabs
                    tabs={tabs}
                    value={activeTab}
                    onChange={v => setActiveTab(v as TabId)}
                    hasInnerPopup={true}
                    header={
                        <Popup.Header
                            title={isEdit ? t('editLectureTitle') : t('manageLecture')}
                            onClose={show.close}
                            endSlot={
                                <css.headerEnd>
                                    <Switcher
                                        checked={values.active}
                                        onChange={e => setFieldValue('active', e.target.checked)}
                                        label={{on: t('toggleActive'), off: t('toggleInactive'), show: true}}
                                        ariaLabel={t('toggleActive')}
                                    />
                                    <css.kebab type="button" aria-label="more">
                                        <KebabDots />
                                    </css.kebab>
                                </css.headerEnd>
                            }
                        />
                    }
                >
                    <css.form placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
                        {activeTabContent}
                    </css.form>
                    <css.footer>
                        <Button variant="neutral" size="category" onClick={show.close} type="button" disabled={isSubmitting}>
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="brand"
                            size="category"
                            onClick={submitForm}
                            type="button"
                            xhrState={(isSubmitting ? XHR_LOADING_STATE : XHR_SUCCESS_STATE) as TXHR}
                        >
                            {isEdit ? t('saveLecture') : t('createLecture')}
                        </Button>
                    </css.footer>
                    <LecturersSelectPopup show={lecturersShow} />
                    <HallSelectPopup show={hallShow} />
                    <TimeSlotSelectPopup show={slotShow} />
                    <DaySelectPopup show={dayShow} />
                </Popup.Tabs>
            )}
        </FormContext>
    );
};

export default ManageLecturePopup;
